import React from 'react';
import { Link } from '@inertiajs/react';
import { Seo, article } from '~/marketing/Seo';
import { Button } from '~/marketing/ui';
import { formatMeasuredOn } from '~/components/data/format.js';
import { routes } from '~/marketing/routes';
import { postAt } from '~/marketing/blog';
import { quoted as Q } from '~/marketing/marketingData';
import { said } from '~/marketing/research/figures';

/* What every blog post shares: the header, the prose styles, the source note
   and the closing button.

   **A post's title, date and summary are its entry in `blog/posts.json`**, read
   here by path, so the post, the blog list and the home page cannot describe
   it three ways. Prose is a 70ch column, the width the marketing pages use. */

export const body = { color: 'var(--text-body)', maxWidth: '70ch', margin: 0 };
export const note = { ...body, fontSize: 'var(--fs-body-sm)' };
const rule = { borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-3)' };

export const P = ({ children }) => <p style={body}>{children}</p>;

export const H2 = ({ children }) => <div style={rule}><h2 style={{ fontSize: 'var(--fs-h2)' }}>{children}</h2></div>;

export const Section = ({ title, children }) => (
  <section style={{ display: 'grid', gap: 'var(--space-5)' }}>
    <H2>{title}</H2>
    {children}
  </section>
);

export const UL = ({ children }) => (
  <ul style={{ ...body, paddingLeft: '1.25em', display: 'grid', gap: 'var(--space-3)' }}>{children}</ul>
);

/* A link to another post, by its path. Throws when the path is not a listed
   post, so a renamed post fails the build rather than leaving a dead link. */
export function PostLink({ to, children }) {
  const post = postAt(to);
  if (!post) throw new Error(`PostLink: no post at ${to}`);
  return <Link href={post.path}>{children ?? post.title}</Link>;
}

export const BENCHMARK = '/blog/local-services-benchmark';

/* Where a post's figures come from, said once: the engines and the dates, the
   two things a figure has to carry to be checkable. `lead` names the figures,
   because not every post's numbers are all the benchmark's. */
export const Sources = ({ lead = 'The figures in this post come from' }) => (
  <p style={note}>
    {lead} <PostLink to={BENCHMARK}>our benchmark of {said.industries} local industries</PostLink>, collected on {said.dates}. {Q.benchmark_scope}
  </p>
);

export function Cta({ heading, caption, label, href }) {
  return (
    <section style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-8)', display: 'flex', gap: 'var(--space-6)', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
      <div style={{ display: 'grid', gap: 6 }}>
        <h2 style={{ fontSize: 'var(--fs-h2)' }}>{heading}</h2>
        {caption && <span className="sp-caption">{caption}</span>}
      </div>
      <Button size="lg" href={href}>{label}</Button>
    </section>
  );
}

// The free check's button, as every post closing on it words it.
export const GraderCta = ({ heading }) => (
  <Cta heading={heading} caption="One question, five AI assistants, the report emailed to you." label="Check your visibility" href={routes.grader} />
);

/* One post. `lede` is the opening paragraph, set larger; `gap` is wider on the
   benchmark post, whose sections carry tables. */
export function Post({ path, lede, gap = 'var(--space-12)', children }) {
  const post = postAt(path);
  return (
    <>
      <Seo
        title={post.title}
        description={post.summary}
        path={post.path}
        schema={[ article({ headline: post.title, path: post.path, published: post.published }) ]}
      />
      <main className="sp-page" style={{ paddingBlock: 'clamp(40px,5vw,80px)', display: 'grid', gap }}>
        <header style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: '66ch' }}>
          <span className="sp-label"><Link href={routes.blog}>Blog</Link> · <span className="sp-mono">{formatMeasuredOn(post.published)}</span></span>
          <h1 style={{ fontSize: 'var(--fs-display-2)', lineHeight: 1.06 }}>{post.title}</h1>
          {lede && <p style={{ color: 'var(--text-body)', fontSize: 'var(--fs-body-lg)', margin: 0 }}>{lede}</p>}
        </header>
        {children}
      </main>
    </>
  );
}
